/**
 * Mise en page du diagramme alluvial « Vagues de décision ».
 *
 * Chaque colonne empile ses bandes en conservant l'aire : la somme des hauteurs vaut
 * toujours l'effectif total. L'axe Y se lit donc comme un rang (0 % en haut = 1er) et
 * non comme une valeur absolue ; le rang moyen exact d'une branche reste dans son
 * infobulle.
 *
 * Le nœud d'une colonne n'est qu'un repère étroit : c'est le ruban qui le relie au
 * suivant qui porte le dessin, pour que la figure se lise comme un courant se divisant
 * plutôt que comme une suite d'histogrammes.
 */

export interface DecisionFlowNode {
  key: string;
  label: string;
  columnIndex: number;
  count: number;
  meanRankPct: number;
  sdRankPct: number;
  ciHalfWidth: number;
  seRankPct: number;
  seDealScore: number;
  medianRankPct: number;
  p25RankPct: number;
  p75RankPct: number;
  meanDealScore: number;
  bestRank: number;
  worstRank: number;
  cards: number[];
  /** Centile de points cumulés de la branche, par index de colonne (0 = en tête). */
  runningPct: number[];
  isResidual: boolean;
  significant: boolean;
  pathKeys: string[];
  ecartKey: string | null;
  children: DecisionFlowNode[];
}

export type DecisionFlowColumnKind = 'start' | 'ecart' | 'trick';

export interface DecisionFlowColumn {
  index: number;
  kind: DecisionFlowColumnKind;
  trickIndex: number | null;
  label: string;
  groupCount: number;
  eta2: number;
  eta2Adj: number;
  eta2Cum: number;
  eta2CumAdj: number;
  hasSplit: boolean;
}

export interface DecisionFlowReport {
  differeId: number;
  dealIndex: number;
  playerCount: number;
  totalPlayersOnDeal: number;
  dealScoreRange: { min: number; max: number };
  contract: number;
  contractLabel: string;
  takerPosition: number;
  isAttackDeal: boolean;
  humanIsTaker: boolean;
  hasEcartColumn: boolean;
  ecartAvailable: boolean;
  ecartUnavailableReason: string | null;
  trickCount: number;
  columns: DecisionFlowColumn[];
  root: DecisionFlowNode;
  params: {
    population: 'all' | 'topX';
    topX: number;
    groupEquiv: boolean;
    lowRankLimit: number;
    ecartGrouping: 'exact' | 'profile';
    includeEcart: boolean;
    minCount: number;
    maxChildren: number;
  };
}

export interface FlowBand {
  id: string;
  parentId: string | null;
  node: DecisionFlowNode;
  column: DecisionFlowColumn;
  /** Rang de la colonne parmi les colonnes affichées. */
  slot: number;
  x: number;
  y: number;
  width: number;
  height: number;
  /** La branche prolonge un nœud terminal : aucune décision nouvelle à cette colonne. */
  isContinuation: boolean;
}

export interface FlowRibbon {
  id: string;
  fromId: string;
  toId: string;
  path: string;
  meanRankPct: number;
  isResidual: boolean;
}

export interface FlowLayout {
  bands: FlowBand[];
  ribbons: FlowRibbon[];
  columns: Array<{ column: DecisionFlowColumn; slot: number; x: number; width: number }>;
  width: number;
  height: number;
}

/**
 * `wave` — arrivée libre sur l'échelle de performance (même grandeur que la couleur) :
 * plus une sous-vague est verte, plus elle arrive haut. Au départ de chaque intervalle,
 * les sœurs sont classées vert→rouge dans le créneau de leur mère, ce qui interdit les
 * croisements fraternels tout en laissant une branche rouge issue d'une mère verte
 * plonger à travers les lignées voisines.
 *
 * `branch` — les enfants restent dans le créneau de leur parent. Aucun croisement : utile
 * pour suivre une lignée de décisions à la trace.
 *
 * `flux` — chaque colonne est réordonnée sur les points de cartes cumulés à ce pli
 * (indépendamment de la couleur, qui reste le classement final).
 */
export type FlowOrdering = 'wave' | 'branch' | 'flux';

export interface FlowLayoutOptions {
  /**
   * Largeur du nœud à la colonne. Étroite, elle laisse le ruban occuper l'intervalle
   * et le graphique se lit comme un courant ; large, elle redonne un effet de barres,
   * nécessaire seulement pour loger le nom des cartes.
   */
  bandWidth: number;
  /** Distance entre les bords gauches de deux colonnes consécutives. */
  columnPitch: number;
  plotHeight: number;
  /** Hauteur minimale d'une bande, dans la limite du créneau que son effectif lui vaut. */
  minBandHeight: number;
  /** Retrait vertical séparant deux bandes voisines. */
  bandGap: number;
  /**
   * Part de la hauteur occupée par le flux, le reste étant laissé en marge haute et basse.
   * En deçà de 1, les vagues respirent au lieu de remplir le cadre bord à bord.
   */
  fillRatio: number;
  ordering: FlowOrdering;
  /**
   * Performance normalisée d'une branche (0 = meilleur / haut, 1 = pire / bas).
   * Requis en mode `wave` ; ignoré sinon. Doit être la même grandeur que la couleur.
   */
  performanceOf?: (node: DecisionFlowNode) => number;
}

/** Largeur de la bande unique de la colonne « Départ ». */
const START_BAND_WIDTH = 16;

export const DEFAULT_FLOW_LAYOUT_OPTIONS: FlowLayoutOptions = {
  bandWidth: 5,
  columnPitch: 88,
  plotHeight: 460,
  minBandHeight: 2,
  bandGap: 1.5,
  fillRatio: 1,
  ordering: 'wave',
};

/**
 * Colonnes à afficher : le départ, puis les colonnes retenues jusqu'à `maxColumnIndex`.
 * `onlyDivergent` masque les plis où aucune branche ne se subdivise — sur une donne
 * réelle ils occupent l'essentiel de la largeur sans rien apprendre.
 */
export function visibleColumnIndexes(
  columns: DecisionFlowColumn[],
  options: { maxColumnIndex: number; onlyDivergent: boolean },
): number[] {
  const visible: number[] = [];
  for (const column of columns) {
    if (column.index > options.maxColumnIndex) break;
    if (column.index === 0) {
      visible.push(0);
      continue;
    }
    if (options.onlyDivergent && !column.hasSplit) continue;
    visible.push(column.index);
  }
  return visible;
}

/**
 * Descendants du nœud à la prochaine colonne affichée.
 *
 * Les colonnes masquées n'ont par construction aucune subdivision, la traversée
 * est donc sans perte. Un nœud terminal rencontré en chemin est remonté tel quel
 * pour que la partition de l'effectif reste complète.
 */
function nextVisibleDescendants(
  node: DecisionFlowNode,
  visible: Set<number>,
  maxColumnIndex: number,
): DecisionFlowNode[] {
  const out: DecisionFlowNode[] = [];
  for (const child of node.children) {
    if (child.columnIndex > maxColumnIndex) continue;
    if (visible.has(child.columnIndex)) {
      out.push(child);
      continue;
    }
    const deeper = nextVisibleDescendants(child, visible, maxColumnIndex);
    if (deeper.length > 0) out.push(...deeper);
    else out.push(child);
  }
  return out;
}

interface PendingBand {
  id: string;
  parentId: string | null;
  node: DecisionFlowNode;
  isContinuation: boolean;
}

/** Position « flux » : points cumulés au pli, avec repli sur le rang final. */
function runningPosition(band: PendingBand, columnIndex: number): number {
  return band.node.runningPct[columnIndex] ?? band.node.meanRankPct;
}

function compareByValue(
  a: PendingBand,
  b: PendingBand,
  valueOf: (band: PendingBand) => number,
): number {
  const pa = valueOf(a);
  const pb = valueOf(b);
  if (pa !== pb) return pa - pb;
  if (a.node.count !== b.node.count) return b.node.count - a.node.count;
  return a.id.localeCompare(b.id);
}

/**
 * Ordre vertical des bandes d'une colonne.
 *
 * `wave` / `flux` — classement global (performance ou points cumulés) : une sous-vague
 * arrive librement sur l'échelle Y. Les croisements naissent entre lignées distinctes.
 *
 * `branch` — les enfants restent dans le créneau de leur parent. Aucun croisement.
 */
function orderColumn(
  pending: PendingBand[],
  previous: FlowBand[] | null,
  options: FlowLayoutOptions,
  columnIndex: number,
): PendingBand[] {
  const { ordering, performanceOf } = options;
  const valueOf = ordering === 'wave' && performanceOf
    ? (band: PendingBand) => performanceOf(band.node)
    : (band: PendingBand) => runningPosition(band, columnIndex);
  const byValue = (a: PendingBand, b: PendingBand) => compareByValue(a, b, valueOf);

  if (ordering === 'wave' || ordering === 'flux' || previous === null) {
    return [...pending].sort(byValue);
  }

  const byParent = new Map<string, PendingBand[]>();
  for (const entry of pending) {
    const key = entry.parentId ?? '';
    const siblings = byParent.get(key);
    if (siblings) siblings.push(entry);
    else byParent.set(key, [entry]);
  }

  const ordered: PendingBand[] = [];
  for (const parent of previous) {
    const siblings = byParent.get(parent.id);
    if (!siblings) continue;
    // Vert en haut de la mère, rouge en bas — même ordre que l'arrivée, donc pas de
    // croisement fraternel ; les plongées ne croisent que des cousines.
    ordered.push(...siblings.sort(byValue));
    byParent.delete(parent.id);
  }
  // Filet de sécurité : une bande orpheline ne doit pas disparaître du rendu.
  for (const orphans of byParent.values()) ordered.push(...orphans.sort(byValue));
  return ordered;
}

function cubicRibbon(
  x0: number,
  x1: number,
  topStart: number,
  topEnd: number,
  bottomStart: number,
  bottomEnd: number,
): string {
  const mid = (x0 + x1) / 2;
  return [
    `M ${x0} ${topStart}`,
    `C ${mid} ${topStart}, ${mid} ${topEnd}, ${x1} ${topEnd}`,
    `L ${x1} ${bottomEnd}`,
    `C ${mid} ${bottomEnd}, ${mid} ${bottomStart}, ${x0} ${bottomStart}`,
    'Z',
  ].join(' ');
}

export function buildFlowLayout(
  report: DecisionFlowReport,
  visibleIndexes: number[],
  options: FlowLayoutOptions = DEFAULT_FLOW_LAYOUT_OPTIONS,
): FlowLayout {
  const columnByIndex = new Map(report.columns.map(column => [column.index, column]));
  const slots = visibleIndexes.filter(index => columnByIndex.has(index));
  const total = report.root.count;

  if (slots.length === 0 || total === 0) {
    return { bands: [], ribbons: [], columns: [], width: 0, height: options.plotHeight };
  }

  const visible = new Set(slots);
  const maxColumnIndex = slots[slots.length - 1]!;
  const scale = (options.plotHeight * options.fillRatio) / total;
  const topMargin = (options.plotHeight * (1 - options.fillRatio)) / 2;

  const bands: FlowBand[] = [];
  const bandById = new Map<string, FlowBand>();
  const ribbons: FlowRibbon[] = [];

  let pending: PendingBand[] = [{
    id: 'root',
    parentId: null,
    node: report.root,
    isContinuation: false,
  }];
  let previous: FlowBand[] | null = null;

  for (let slot = 0; slot < slots.length; slot++) {
    const columnIndex = slots[slot]!;
    const column = columnByIndex.get(columnIndex)!;
    const x = slot * options.columnPitch;

    const ordered = orderColumn(pending, previous, options, columnIndex);

    // La colonne de départ porte l'effectif entier en une bande : il faut qu'on la voie
    // comme telle, là où les nœuds suivants ne sont que des repères entre deux courants.
    const width = slot === 0 ? Math.max(options.bandWidth, START_BAND_WIDTH) : options.bandWidth;

    let cursor = topMargin;
    const placed: FlowBand[] = [];
    for (const entry of ordered) {
      const span = entry.node.count * scale;
      // Le plancher de lisibilité ne peut pas déborder du créneau : sur une branche d'un
      // seul joueur il dépasserait la part qui lui revient, mordrait sur la bande voisine
      // et sortirait du créneau de son parent, faisant croire à un croisement.
      const height = Math.min(span, Math.max(options.minBandHeight, span - options.bandGap));
      const band: FlowBand = {
        id: entry.id,
        parentId: entry.parentId,
        node: entry.node,
        column,
        slot,
              x,
              y: cursor,
              width,
              height,
        isContinuation: entry.isContinuation,
      };
      cursor += span;
      placed.push(band);
      bands.push(band);
      bandById.set(band.id, band);
    }

    if (slot > 0) {
      appendRibbons(placed, bandById, ribbons, options);
    }
    previous = placed;

    if (slot === slots.length - 1) break;

    const next: PendingBand[] = [];
    for (const band of placed) {
      const children = nextVisibleDescendants(band.node, visible, maxColumnIndex);
      if (children.length === 0) {
        next.push({
          id: `${band.id}>~`,
          parentId: band.id,
          node: band.node,
          isContinuation: true,
        });
        continue;
      }
      for (const child of children) {
        next.push({
          id: `${band.id}>${child.columnIndex}:${child.key}`,
          parentId: band.id,
          node: child,
          isContinuation: false,
        });
      }
    }
    pending = next;
  }

  return {
    bands,
    ribbons,
    columns: slots.map((index, slot) => ({
      column: columnByIndex.get(index)!,
      slot,
      x: slot * options.columnPitch,
      width: options.bandWidth,
    })),
    width: (slots.length - 1) * options.columnPitch + options.bandWidth,
    height: options.plotHeight,
  };
}

/**
 * Rubans d'une colonne vers la précédente.
 *
 * Les enfants d'un même parent reçoivent des tranches contiguës de la bande parente,
 * allouées de la plus verte (haut) à la plus rouge (bas) : aucun croisement fraternel.
 * Les croisements subsistants, entre fratries, sont volontaires — une sous-vague rouge
 * issue d'une mère verte peut plonger à travers les lignées voisines.
 */
function appendRibbons(
  placed: FlowBand[],
  bandById: Map<string, FlowBand>,
  ribbons: FlowRibbon[],
  options: FlowLayoutOptions,
): void {
  const byParent = new Map<string, FlowBand[]>();
  for (const band of placed) {
    if (!band.parentId) continue;
    const siblings = byParent.get(band.parentId);
    if (siblings) siblings.push(band);
    else byParent.set(band.parentId, [band]);
  }

  const performanceOf = options.performanceOf;
  const sortSiblings = (a: FlowBand, b: FlowBand) => {
    if (options.ordering === 'wave' && performanceOf) {
      const pa = performanceOf(a.node);
      const pb = performanceOf(b.node);
      if (pa !== pb) return pa - pb;
    }
    return a.y - b.y;
  };

  for (const [parentId, siblings] of byParent) {
    const parent = bandById.get(parentId);
    if (!parent) continue;
    const totalCount = siblings.reduce((sum, band) => sum + band.node.count, 0);
    if (totalCount <= 0) continue;

    siblings.sort(sortSiblings);
    let offset = parent.y;
    for (const band of siblings) {
      const slice = (band.node.count / totalCount) * parent.height;
      ribbons.push({
        id: `${parentId}->${band.id}`,
        fromId: parentId,
        toId: band.id,
        path: cubicRibbon(
          parent.x + parent.width,
          band.x,
          offset,
          band.y,
          offset + slice,
          band.y + band.height,
        ),
        meanRankPct: band.node.meanRankPct,
        isResidual: band.node.isResidual,
      });
      offset += slice;
    }
  }
}

export interface DecisionImpact {
  band: FlowBand;
  /** Gain (négatif) ou perte (positif) de percentile par rapport au parent. */
  delta: number;
  parentMeanRankPct: number;
}

/**
 * Décisions les plus lourdes de conséquence : écart au rang moyen du parent, restreint
 * aux branches statistiquement distinguables de leur parent et pondéré par l'effectif.
 */
export function topDecisionImpacts(layout: FlowLayout, limit = 5): DecisionImpact[] {
  const bandById = new Map(layout.bands.map(band => [band.id, band]));
  const impacts: DecisionImpact[] = [];

  for (const band of layout.bands) {
    if (!band.parentId || band.isContinuation || band.node.isResidual) continue;
    if (!band.node.significant) continue;
    const parent = bandById.get(band.parentId);
    if (!parent) continue;
    impacts.push({
      band,
      delta: band.node.meanRankPct - parent.node.meanRankPct,
      parentMeanRankPct: parent.node.meanRankPct,
    });
  }

  return impacts
    .sort((a, b) => Math.abs(b.delta) * b.band.node.count - Math.abs(a.delta) * a.band.node.count)
    .slice(0, limit);
}
